import React, { useState, useEffect } from 'react';
import { useUrlState } from '../hooks/useUrlParams';
import { carDealerAPI, carBrandAPI, cityAPI } from '../services/api';
import SearchableDropdown from '../components/SearchableDropdown';
import './Brands.css';

function CarDealers() {
  const [dealers, setDealers] = useState([]);
  const [brands, setBrands] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Sync state
  const [syncing, setSyncing] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [syncProgress, setSyncProgress] = useState({ currentCity: '', currentBrand: '', cityIndex: 0, brandIndex: 0, totalCities: 0, totalBrands: 0 });
  const [syncResults, setSyncResults] = useState([]);
  const [syncComplete, setSyncComplete] = useState(false);
  const [showSyncOptions, setShowSyncOptions] = useState(false);
  const [lastSyncProgress, setLastSyncProgress] = useState(null);

  // Brand+City sync modal state
  const [showBrandCitySync, setShowBrandCitySync] = useState(false);
  const [syncBrands, setSyncBrands] = useState([]);
  const [allCities, setAllCities] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [previewData, setPreviewData] = useState([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [syncingItem, setSyncingItem] = useState(null);
  const [syncError, setSyncError] = useState('');
  const [syncingAll, setSyncingAll] = useState(false);

  // Pagination state
  const [page, setPage] = useUrlState('page', 1);
  const [limit, setLimit] = useUrlState('limit', 10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Filter state
  const [search, setSearch] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [brandFilter, setBrandFilter] = useState('');

  const fetchBrands = async () => {
    try {
      const response = await carBrandAPI.getAll({ limit: 100 });
      const brandsData = response.data.data || response.data || [];
      setBrands(brandsData);
      return brandsData;
    } catch (err) { console.error('Failed to fetch brands:', err); return []; }
  };

  const fetchCities = async () => {
    try {
      const response = await carDealerAPI.getCities();
      setCities(response.data || []);
    } catch (err) { console.error('Failed to fetch cities:', err); }
  };

  const fetchDealers = async (currentPage, currentLimit, currentSearch, currentCity, currentBrand) => {
    try {
      setLoading(true);
      const params = { page: currentPage, limit: currentLimit };
      if (currentSearch) params.search = currentSearch;
      if (currentCity) params.city = currentCity;
      if (currentBrand) params.brandSlug = currentBrand;

      const response = await carDealerAPI.getAll(params);

      if (Array.isArray(response.data)) {
        setDealers(response.data); setTotal(response.data.length); setTotalPages(1);
      } else {
        setDealers(response.data.data || []); setTotal(response.data.pagination?.total || 0); setTotalPages(response.data.pagination?.totalPages || 0);
      }
      setError('');
    } catch (err) { console.error('Fetch error:', err); setError('Failed to fetch dealers'); setDealers([]); }
    finally { setLoading(false); }
  };

  const fetchSyncProgress = async () => {
    try { const response = await carDealerAPI.getSyncProgress(); setLastSyncProgress(response.data); }
    catch (err) { console.error('Failed to fetch sync progress:', err); }
  };

  useEffect(() => { fetchBrands(); fetchCities(); fetchSyncProgress(); }, []);
  useEffect(() => { fetchDealers(page, limit, search, cityFilter, brandFilter); }, [page, limit, search, cityFilter, brandFilter]);

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this dealer?')) {
      try { await carDealerAPI.delete(id); fetchDealers(page, limit, search, cityFilter, brandFilter); }
      catch (err) { setError('Failed to delete dealer'); }
    }
  };

  const handleSyncAll = async (mode = 'smart') => {
    setShowSyncOptions(false); setSyncing(true); setShowSyncModal(true); setSyncComplete(false); setSyncResults([]);
    let progressMessage = 'Processing all brands and cities...';
    let subMessage = 'This may take several minutes';
    if (mode === 'smart') { progressMessage = 'Smart Sync: Checking for changes...'; subMessage = 'Skipping cities where DB matches API count'; }
    else if (mode === 'resume') { progressMessage = 'Resuming from last position...'; subMessage = lastSyncProgress?.lastCityName ? `Continuing from ${lastSyncProgress.lastCityName}` : 'Starting sync'; }
    else if (mode === 'full') { progressMessage = 'Full Sync: Processing all cities...'; subMessage = 'Re-syncing all data from beginning'; }

    setSyncProgress({ currentCity: progressMessage, currentBrand: subMessage, cityIndex: 0, brandIndex: 0, totalCities: 1, totalBrands: 1 });

    try {
      const response = await carDealerAPI.syncAll(mode);
      const data = response.data;
      if (data.success || data.totalSynced !== undefined) {
        setSyncResults(data.results || []);
        setSyncProgress({ currentCity: 'Complete', currentBrand: `Synced ${data.totalSynced || 0} dealers${data.skippedCities ? `, skipped ${data.skippedCities} cities` : ''}`, cityIndex: data.totalCities || 0, brandIndex: data.totalBrands || 0, totalCities: data.totalCities || 0, totalBrands: data.totalBrands || 0 });
      } else { setError(data.error || 'Sync failed'); }
    } catch (err) { console.error('Sync error:', err); setError(err.response?.data?.error || 'Failed to sync dealers'); }

    setSyncComplete(true); setSyncing(false);
    fetchDealers(page, limit, search, cityFilter, brandFilter); fetchCities(); fetchSyncProgress();
  };

  const handleResetSyncProgress = async () => {
    try { await carDealerAPI.resetSyncProgress(); setLastSyncProgress(null); setShowSyncOptions(false); }
    catch (err) { setError('Failed to reset sync progress'); }
  };

  const closeSyncModal = () => setShowSyncModal(false);

  // Brand+City sync functions
  const openBrandCitySync = async () => {
    setShowSyncOptions(false); setShowBrandCitySync(true); setSyncError(''); setPreviewData([]); setSelectedBrand(''); setSelectedCity('');
    try {
      const [brandsRes, citiesRes] = await Promise.all([carDealerAPI.getCarBrands(), cityAPI.getAll({ limit: 2000 })]);
      setSyncBrands(brandsRes.data || []);
      setAllCities(citiesRes.data.data || citiesRes.data || []);
    } catch (err) { setSyncError('Failed to load brands/cities'); }
  };

  const handleFetchPreview = async () => {
    if (!selectedBrand || !selectedCity) { setSyncError('Please select both brand and city'); return; }
    setLoadingPreview(true); setSyncError(''); setPreviewData([]);
    try {
      const brand = syncBrands.find(b => b.slug === selectedBrand);
      const city = allCities.find(c => c.name === selectedCity);
      const response = await carDealerAPI.preview({ brandSlug: selectedBrand, cityId: city?.id, cityName: selectedCity, brandName: brand?.brandName });
      setPreviewData(response.data.dealers || []);
    } catch (err) { setSyncError(err.response?.data?.error || 'Failed to fetch preview'); }
    finally { setLoadingPreview(false); }
  };

  const handleSyncSingle = async (item) => {
    setSyncingItem(item.dealerId || item.name);
    try {
      await carDealerAPI.syncSingle(item);
      setPreviewData(prev => prev.map(p => (p.dealerId === item.dealerId && p.name === item.name) ? { ...p, existsInDb: true } : p));
      fetchDealers(page, limit, search, cityFilter, brandFilter); fetchCities();
    } catch (err) { setSyncError(err.response?.data?.error || 'Failed to sync item'); }
    finally { setSyncingItem(null); }
  };

  const handleSyncAllPreview = async () => {
    setSyncingAll(true);
    for (const item of previewData) {
      try {
        await carDealerAPI.syncSingle(item);
        setPreviewData(prev => prev.map(p => (p.dealerId === item.dealerId && p.name === item.name) ? { ...p, existsInDb: true } : p));
      } catch (err) { console.error('Failed to sync:', item.name, err.message); }
    }
    setSyncingAll(false); fetchDealers(page, limit, search, cityFilter, brandFilter); fetchCities();
  };

  const closeBrandCitySync = () => { setShowBrandCitySync(false); setPreviewData([]); setSyncError(''); };

  const handlePageChange = (newPage) => { if (newPage >= 1 && newPage <= totalPages) setPage(newPage); };
  const handleLimitChange = (e) => { setLimit(parseInt(e.target.value)); setPage(1); };
  const handleSearchChange = (e) => { setSearch(e.target.value); setPage(1); };
  const handleBrandFilterChange = (value) => { setBrandFilter(value); setPage(1); };
  const getTotalSynced = () => syncResults.reduce((sum, r) => sum + (r.count || 0), 0);
  const getProgressPercent = () => {
    if (syncComplete) return 100;
    if (syncing && syncProgress.totalCities <= 1) return -1;
    const totalOps = syncProgress.totalCities * syncProgress.totalBrands;
    const currentOp = ((syncProgress.cityIndex - 1) * syncProgress.totalBrands) + syncProgress.brandIndex;
    return totalOps > 0 ? Math.round((currentOp / totalOps) * 100) : 0;
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    if (endPage - startPage + 1 < maxVisiblePages) startPage = Math.max(1, endPage - maxVisiblePages + 1);
    for (let i = startPage; i <= endPage; i++) {
      pages.push(<button key={i} className={`pagination-btn ${page === i ? 'active' : ''}`} onClick={() => handlePageChange(i)}>{i}</button>);
    }
    return (
      <div className="pagination">
        <div className="pagination-info">Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, total)} of {total} entries</div>
        <div className="pagination-controls">
          <button className="pagination-btn" onClick={() => handlePageChange(1)} disabled={page === 1}>First</button>
          <button className="pagination-btn" onClick={() => handlePageChange(page - 1)} disabled={page === 1}>Prev</button>
          {pages}
          <button className="pagination-btn" onClick={() => handlePageChange(page + 1)} disabled={page === totalPages}>Next</button>
          <button className="pagination-btn" onClick={() => handlePageChange(totalPages)} disabled={page === totalPages}>Last</button>
        </div>
      </div>
    );
  };

  return (
    <div className="brands-page">
      {/* Bulk Sync Progress Modal */}
      {showSyncModal && (
        <div className="modal-overlay">
          <div className="sync-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{syncComplete ? 'Sync Complete' : 'Syncing Car Dealers...'}</h2>
              {syncComplete && <button className="modal-close" onClick={closeSyncModal}>&times;</button>}
            </div>
            <div className="modal-body sync-modal-body">
              {syncing && (
                <div className="sync-progress">
                  <div className="spinner"></div>
                  <p className="sync-status"><strong>{syncProgress.currentCity}</strong></p>
                  <p className="sync-counts">{syncProgress.currentBrand}</p>
                  <div className="progress-bar">
                    <div className={`progress-fill ${getProgressPercent() === -1 ? 'indeterminate' : ''}`} style={{ width: getProgressPercent() === -1 ? '30%' : `${getProgressPercent()}%` }}></div>
                  </div>
                  <p className="progress-percent">{getProgressPercent() === -1 ? 'Please wait...' : `${getProgressPercent()}%`}</p>
                </div>
              )}
              {syncComplete && syncResults.length > 0 && (
                <div className="sync-results">
                  <h3>Total: {getTotalSynced()} dealers synced</h3>
                  <div className="results-grid">
                    {syncResults.filter(r => r.count > 0).map((result, index) => (
                      <div key={index} className="result-item success">
                        <span className="city-name">{result.city}</span>
                        <span className="count">{result.count} dealers</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {syncComplete && (<div className="modal-footer"><button className="btn btn-primary" onClick={closeSyncModal}>Done</button></div>)}
          </div>
        </div>
      )}

      {/* Brand+City Sync Modal */}
      {showBrandCitySync && (
        <div className="modal-overlay">
          <div className="sync-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px' }}>
            <div className="modal-header">
              <h2>Sync by Brand & City</h2>
              <button className="modal-close" onClick={closeBrandCitySync}>&times;</button>
            </div>
            <div className="modal-body sync-modal-body">
              <div className="sync-filters" style={{ display: 'flex', gap: '10px', marginBottom: '15px', alignItems: 'flex-end' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '14px' }}>Brand</label>
                  <SearchableDropdown
                    options={syncBrands.map(b => ({ value: b.slug, label: b.brandName }))}
                    value={selectedBrand}
                    onChange={setSelectedBrand}
                    placeholder="Search brand..."
                    allLabel="Select Brand"
                    dark
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '14px' }}>City</label>
                  <SearchableDropdown
                    options={allCities.map(c => ({ value: c.name, label: c.name }))}
                    value={selectedCity}
                    onChange={setSelectedCity}
                    placeholder="Search city..."
                    allLabel="Select City"
                    dark
                  />
                </div>
                <button className="btn btn-primary" onClick={handleFetchPreview} disabled={loadingPreview || !selectedBrand || !selectedCity}>
                  {loadingPreview ? 'Fetching...' : 'Fetch Preview'}
                </button>
              </div>

              {syncError && <div className="alert alert-error" style={{ marginBottom: '10px' }}>{syncError}</div>}
              {loadingPreview && (<div className="sync-progress"><div className="spinner"></div><p>Fetching dealers from API...</p></div>)}

              {!loadingPreview && previewData.length > 0 && (
                <div className="preview-brands-grid">
                  {previewData.map((item, idx) => (
                    <div key={item.dealerId || idx} className="preview-brand-card">
                      <div className="preview-brand-info" style={{ flex: 1 }}>
                        <h4 style={{ fontSize: '14px', margin: '0 0 4px' }}>{item.name}</h4>
                        <span style={{ fontSize: '12px', color: '#666' }}>{item.locality || item.city}{item.phone ? ` | ${item.phone}` : ''}</span>
                      </div>
                      <button
                        className={`btn btn-sm ${item.existsInDb ? 'btn-update' : 'btn-add'}`}
                        onClick={() => handleSyncSingle(item)}
                        disabled={syncingItem === (item.dealerId || item.name) || syncingAll}
                      >
                        {syncingItem === (item.dealerId || item.name) ? 'Syncing...' : (item.existsInDb ? 'Update' : 'Add')}
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {!loadingPreview && previewData.length === 0 && selectedBrand && selectedCity && (
                <div className="empty-state"><p>No dealers found for this brand and city.</p></div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closeBrandCitySync}>Close</button>
              {previewData.length > 0 && (
                <button className="btn btn-sync" onClick={handleSyncAllPreview} disabled={syncingAll}>
                  {syncingAll ? 'Syncing All...' : `Sync All (${previewData.length})`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="page-header">
        <h1>Car Dealers</h1>
        <div className="header-actions">
          <button className="btn btn-primary" onClick={openBrandCitySync}>Sync by Brand & City</button>
          <div className="sync-dropdown">
            <button className="btn btn-sync" onClick={() => setShowSyncOptions(!showSyncOptions)} disabled={syncing}>
              {syncing ? 'Syncing...' : 'Sync All Data'} &#9660;
            </button>
            {showSyncOptions && (
              <div className="sync-options-menu">
                <button onClick={() => handleSyncAll('smart')} className="sync-option"><strong>Smart Sync</strong><span>Skip cities where count matches</span></button>
                {lastSyncProgress && lastSyncProgress.status === 'in_progress' && (
                  <button onClick={() => handleSyncAll('resume')} className="sync-option resume"><strong>Resume Sync</strong><span>Continue from {lastSyncProgress.lastCityName || 'last position'}</span></button>
                )}
                <button onClick={() => handleSyncAll('full')} className="sync-option"><strong>Full Sync</strong><span>Re-sync all cities from beginning</span></button>
                {lastSyncProgress && lastSyncProgress.status !== 'not_started' && (
                  <button onClick={handleResetSyncProgress} className="sync-option danger"><strong>Reset Progress</strong><span>Clear sync tracking data</span></button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="card">
        <div className="table-controls">
          <div className="table-filters">
            <input type="text" placeholder="Search by name, locality, city..." value={search} onChange={handleSearchChange} className="search-input" />
            <SearchableDropdown options={cities} value={cityFilter} onChange={(val) => { setCityFilter(val); setPage(1); }} placeholder="Search cities..." allLabel="All Cities" />
            <SearchableDropdown
              options={brands.map(brand => ({ value: String(brand.slug), label: brand.brandName }))}
              value={brandFilter}
              onChange={handleBrandFilterChange}
              placeholder="Search brands..."
              allLabel="All Brands"
            />
          </div>
          <div className="table-options">
            <label>Show <select value={limit} onChange={handleLimitChange}><option value="5">5</option><option value="10">10</option><option value="25">25</option><option value="50">50</option></select> entries</label>
          </div>
        </div>

        {loading ? (
          <div className="loading">Loading...</div>
        ) : dealers.length === 0 ? (
          <div className="empty-state"><p>No dealers found. {search || cityFilter || brandFilter ? 'Try adjusting your filters.' : 'Click "Sync All Data" to fetch dealers!'}</p></div>
        ) : (
          <>
            <table className="data-table">
              <thead><tr><th>ID</th><th>Name</th><th>Brand</th><th>City</th><th>Locality</th><th>Phone</th><th>Pincode</th><th>Actions</th></tr></thead>
              <tbody>
                {dealers.map((dealer) => (
                  <tr key={dealer.id}>
                    <td>{dealer.id}</td>
                    <td><div className="dealer-name">{dealer.name}{dealer.isNexaDealer && <span className="badge nexa-badge">NEXA</span>}</div></td>
                    <td>{dealer.brand || '-'}</td>
                    <td>{dealer.city}</td>
                    <td>{dealer.locality || '-'}</td>
                    <td>{dealer.phone || '-'}</td>
                    <td>{dealer.pincode || '-'}</td>
                    <td>
                      <div className="action-buttons">
                        {dealer.mapUrl && <a href={dealer.mapUrl} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-view">Map</a>}
                        <button className="btn btn-sm btn-danger" onClick={() => handleDelete(dealer.id)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {totalPages > 1 && renderPagination()}
          </>
        )}
      </div>

      <style jsx>{`
        .sync-dropdown { position: relative; display: inline-block; }
        .sync-options-menu { position: absolute; top: 100%; right: 0; background: white; border: 1px solid #ddd; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.15); min-width: 250px; z-index: 1000; margin-top: 5px; }
        .sync-option { display: block; width: 100%; padding: 12px 16px; border: none; background: none; text-align: left; cursor: pointer; border-bottom: 1px solid #eee; }
        .sync-option:last-child { border-bottom: none; }
        .sync-option:hover { background-color: #f5f5f5; }
        .sync-option strong { display: block; color: #333; margin-bottom: 2px; }
        .sync-option span { display: block; font-size: 12px; color: #666; }
        .sync-option.resume { background-color: #e3f2fd; }
        .sync-option.resume:hover { background-color: #bbdefb; }
        .sync-option.danger strong { color: #dc3545; }
        .sync-option.danger:hover { background-color: #ffebee; }
        .sync-progress { text-align: center; padding: 20px; }
        .sync-status { margin: 15px 0 5px; font-size: 16px; }
        .sync-counts { color: #666; font-size: 14px; margin-bottom: 15px; }
        .progress-percent { margin-top: 8px; font-weight: bold; color: #007bff; }
        .sync-results { margin-top: 10px; }
        .sync-results h3 { margin-bottom: 15px; color: #28a745; text-align: center; }
        .results-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 10px; max-height: 300px; overflow-y: auto; }
        .result-item { display: flex; justify-content: space-between; align-items: center; padding: 10px 15px; border-radius: 4px; font-size: 14px; }
        .result-item.success { background-color: #d4edda; color: #155724; }
        .result-item .city-name { font-weight: 500; }
        .result-item .count { font-size: 12px; opacity: 0.8; }
        .progress-bar { width: 100%; height: 10px; background-color: #e9ecef; border-radius: 5px; overflow: hidden; }
        .progress-fill { height: 100%; background-color: #007bff; transition: width 0.3s ease; }
        .progress-fill.indeterminate { animation: indeterminate 1.5s infinite ease-in-out; }
        @keyframes indeterminate { 0% { transform: translateX(-100%); } 50% { transform: translateX(200%); } 100% { transform: translateX(-100%); } }
        .dealer-name { display: flex; align-items: center; gap: 8px; }
        .badge.nexa-badge { background-color: #1a237e; color: white; padding: 2px 6px; border-radius: 3px; font-size: 10px; font-weight: 600; }
      `}</style>
    </div>
  );
}

export default CarDealers;
